import React from 'react';

function EsignWorkflowDiagram({ title = "Bank-Grade Safety & eSign Authentication Suite" }) {
  return (
    <div className="p-4 p-lg-5 rounded-4 shadow-lg my-4 position-relative overflow-hidden" style={{ backgroundColor: '#012b2a', border: '1px solid rgba(204, 255, 0, 0.2)' }}>
      
      {/* Section Header */}
      <div className="text-center mb-5">
        <span className="badge mb-2 px-3 py-2 text-uppercase font-weight-bold" style={{ backgroundColor: 'rgba(204, 255, 0, 0.15)', color: '#ccff00', border: '1px solid rgba(204, 255, 0, 0.4)', letterSpacing: '1px' }}>
          <i className="fa-solid fa-shield-halved me-1"></i> BANK-GRADE SECURITY &amp; ESIGN ARCHITECTURE
        </span>
        <h2 className="heading_text text-white display-6 font-weight-bold">{title}</h2>
        <p className="mx-auto text-white-50" style={{ maxWidth: '680px', fontSize: '0.96rem', lineHeight: '1.7' }}>
          Automate multi-factor biometric authentication, digital contract signatures, Aadhaar OTP verification, and instant audit trails with zero compromises.
        </p>
      </div>

      {/* 2 Grid Columns matching reference diagram */}
      <div className="row g-4 align-items-stretch">
        
        {/* Left Card: Document eSign Flow */}
        <div className="col-lg-6">
          <div className="p-4 rounded-4 h-100 position-relative border d-flex flex-column justify-content-between" style={{ backgroundColor: '#043836', borderColor: 'rgba(204, 255, 0, 0.25)', minHeight: '440px' }}>
            
            {/* Header Badge */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-2">
                <span className="rounded-circle p-2 d-inline-flex align-items-center justify-content-center" style={{ backgroundColor: '#012b2a', border: '1.5px solid #ccff00', width: '36px', height: '36px', color: '#ccff00' }}>
                  <i className="fa-solid fa-file-signature"></i>
                </span>
                <span className="text-white font-weight-bold small">Contract eSign Workflow</span>
              </div>
              <span className="badge px-2 py-1 small" style={{ backgroundColor: 'rgba(204, 255, 0, 0.2)', color: '#ccff00', border: '1px solid #ccff00' }}>
                <i className="fa-solid fa-check me-1"></i>256-Bit SSL Encrypted
              </span>
            </div>

            {/* Central Simulated Document Canvas */}
            <div className="p-4 rounded-4 position-relative mx-auto w-100 border shadow" style={{ backgroundColor: '#012b2a', borderColor: 'rgba(204, 255, 0, 0.3)', maxWidth: '460px' }}>
              
              {/* PDF Document Top Header */}
              <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-danger text-white font-weight-bold px-2 py-1">PDF</span>
                  <span className="text-white small font-weight-bold">Mokshiq_Agreement_2026.pdf</span>
                </div>
                <span className="badge text-dark font-weight-bold px-2 py-1" style={{ backgroundColor: '#ccff00' }}>Ready to Sign</span>
              </div>

              {/* Document Text Line Skeleton */}
              <div className="mb-4">
                <div className="rounded mb-2" style={{ height: '7px', width: '90%', backgroundColor: 'rgba(255, 255, 255, 0.15)' }}></div>
                <div className="rounded mb-2" style={{ height: '7px', width: '95%', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}></div>
                <div className="rounded mb-3" style={{ height: '7px', width: '70%', backgroundColor: 'rgba(255, 255, 255, 0.15)' }}></div>
              </div>

              {/* Floating Interactive Tag Badges */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                <div className="px-3 py-1 rounded-pill small font-weight-bold d-inline-flex align-items-center gap-2" style={{ backgroundColor: 'rgba(37, 99, 235, 0.2)', border: '1px solid #3b82f6', color: '#60a5fa' }}>
                  <i className="fa-solid fa-cloud-arrow-up"></i> Document Uploaded
                </div>
                <div className="px-3 py-1 rounded-pill small font-weight-bold d-inline-flex align-items-center gap-2" style={{ backgroundColor: 'rgba(204, 255, 0, 0.15)', border: '1px solid #ccff00', color: '#ccff00' }}>
                  <i className="fa-solid fa-pen-fancy"></i> Sign Here
                </div>
              </div>

              {/* Digital Signature Box */}
              <div className="p-3 rounded-3 border text-center position-relative" style={{ backgroundColor: '#043836', borderColor: '#ccff00', borderStyle: 'dashed', borderWidth: '2px' }}>
                <div className="font-monospace fw-bold text-lime mb-1" style={{ fontSize: '18px', fontStyle: 'italic', letterSpacing: '1px' }}>
                  Rohit Kushwaha ✍️
                </div>
                <div className="text-white-50 font-weight-bold" style={{ fontSize: '11px' }}>
                  Verified via Aadhaar OTP &amp; Biometric Auth
                </div>
              </div>

            </div>

            {/* Bottom Status */}
            <div className="mt-4 text-center">
              <span className="small text-white-50">Automated multi-signer routing with legal compliance under Indian IT Act 2000.</span>
            </div>

          </div>
        </div>

        {/* Right Card: 4-Node Biometric Authentication Grid */}
        <div className="col-lg-6">
          <div className="p-4 rounded-4 h-100 position-relative border d-flex flex-column justify-content-between" style={{ backgroundColor: '#043836', borderColor: 'rgba(204, 255, 0, 0.25)', minHeight: '440px' }}>
            
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-2">
                <span className="rounded-circle p-2 d-inline-flex align-items-center justify-content-center" style={{ backgroundColor: '#012b2a', border: '1.5px solid #ccff00', width: '36px', height: '36px', color: '#ccff00' }}>
                  <i className="fa-solid fa-fingerprint"></i>
                </span>
                <span className="text-white font-weight-bold small">Multi-Factor Verification Nodes</span>
              </div>
              <span className="badge px-2 py-1 small text-dark font-weight-bold" style={{ backgroundColor: '#ccff00' }}>
                <i className="fa-solid fa-lock me-1"></i>Zero Compromise Security
              </span>
            </div>

            {/* 4 Node Diagram Grid centered around hub */}
            <div className="position-relative p-3 my-auto">
              
              {/* Central Hub Node */}
              <div className="text-center my-3">
                <div className="d-inline-flex flex-column align-items-center justify-content-center rounded-circle shadow-lg p-3" style={{ width: '100px', height: '100px', backgroundColor: '#ccff00', border: '4px solid #ffffff', boxShadow: '0 0 30px rgba(204, 255, 0, 0.5)' }}>
                  <i className="fa-solid fa-file-contract fs-3 text-dark mb-1"></i>
                  <span className="fw-bold text-dark" style={{ fontSize: '11px', lineHeight: '1.1' }}>Secure eSign</span>
                </div>
              </div>

              {/* 4 Surrounding Verification Option Nodes Grid */}
              <div className="row g-3">
                
                {/* Node 1: Aadhaar OTP */}
                <div className="col-6">
                  <div className="p-3 rounded-4 border text-center transition-all" style={{ backgroundColor: '#012b2a', borderColor: 'rgba(204, 255, 0, 0.3)' }}>
                    <div className="rounded-circle mx-auto mb-2 d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(168, 85, 247, 0.2)', border: '1.5px solid #c084fc', color: '#c084fc' }}>
                      <i className="fa-solid fa-id-card fs-5"></i>
                    </div>
                    <div className="text-white font-weight-bold small mb-1">Aadhaar OTP</div>
                    <span className="badge bg-secondary text-white-50" style={{ fontSize: '10px' }}>Instant Auth</span>
                  </div>
                </div>

                {/* Node 2: Biometric */}
                <div className="col-6">
                  <div className="p-3 rounded-4 border text-center transition-all" style={{ backgroundColor: '#012b2a', borderColor: 'rgba(204, 255, 0, 0.3)' }}>
                    <div className="rounded-circle mx-auto mb-2 d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(249, 115, 22, 0.2)', border: '1.5px solid #fb923c', color: '#fb923c' }}>
                      <i className="fa-solid fa-fingerprint fs-5"></i>
                    </div>
                    <div className="text-white font-weight-bold small mb-1">Biometric</div>
                    <span className="badge bg-secondary text-white-50" style={{ fontSize: '10px' }}>Fingerprint Scanner</span>
                  </div>
                </div>

                {/* Node 3: Iris Scan */}
                <div className="col-6">
                  <div className="p-3 rounded-4 border text-center transition-all" style={{ backgroundColor: '#012b2a', borderColor: 'rgba(204, 255, 0, 0.3)' }}>
                    <div className="rounded-circle mx-auto mb-2 d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(59, 130, 246, 0.2)', border: '1.5px solid #60a5fa', color: '#60a5fa' }}>
                      <i className="fa-solid fa-eye fs-5"></i>
                    </div>
                    <div className="text-white font-weight-bold small mb-1">Iris Scan</div>
                    <span className="badge bg-secondary text-white-50" style={{ fontSize: '10px' }}>Retinal Sensor</span>
                  </div>
                </div>

                {/* Node 4: Face Match */}
                <div className="col-6">
                  <div className="p-3 rounded-4 border text-center transition-all" style={{ backgroundColor: '#012b2a', borderColor: 'rgba(204, 255, 0, 0.3)' }}>
                    <div className="rounded-circle mx-auto mb-2 d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(34, 197, 94, 0.2)', border: '1.5px solid #4ade80', color: '#4ade80' }}>
                      <i className="fa-solid fa-face-smile fs-5"></i>
                    </div>
                    <div className="text-white font-weight-bold small mb-1">Face Match</div>
                    <span className="badge bg-secondary text-white-50" style={{ fontSize: '10px' }}>AI Facial Recognition</span>
                  </div>
                </div>

              </div>

            </div>

            {/* Bottom Status */}
            <div className="mt-4 text-center">
              <span className="small text-white-50">Unified API integration supporting Aadhaar OTP, Biometric, Iris &amp; Face Match authentication.</span>
            </div>

          </div>
        </div>

      </div>

    </div>
  );
}

export default EsignWorkflowDiagram;
